import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaSpinner, FaTrophy } from 'react-icons/fa';
import { ModelComparisonPanelProps, ModelMetrics, TrainedModelWithMetrics } from '../types';
import { useModelMetrics } from '../hooks';

/**
 * Panel for comparing metrics between selected models
 * Shows a table with metrics side by side and highlights the best performer
 */
export const ModelComparisonPanel: React.FC<ModelComparisonPanelProps & { sessionId: string }> = memo(({
  models,
  selectedModels,
  sessionId,
  onClose
}) => {
  const { t } = useTranslation('training');
  const { fetchMetricsForModel, metricsMap, loading: metricsLoading } = useModelMetrics(sessionId);
  const [loadingMetrics, setLoadingMetrics] = useState(true);

  // Load metrics for all selected models
  useEffect(() => {
    const loadAllMetrics = async () => {
      setLoadingMetrics(true);
      for (const filename of selectedModels) {
        await fetchMetricsForModel(filename);
      }
      setLoadingMetrics(false);
    };

    if (selectedModels.length > 0) {
      loadAllMetrics();
    }
  }, [selectedModels, fetchMetricsForModel]);

  // Get models with their metrics
  const modelsWithMetrics: TrainedModelWithMetrics[] = models
    .filter(m => selectedModels.includes(m.filename))
    .map(m => ({
      ...m,
      metrics: metricsMap.get(m.filename)
    }));

  // Metric definitions for display
  const metricDefinitions: { key: keyof ModelMetrics; label: string; lowerIsBetter: boolean }[] = [
    { key: 'r2', label: 'R²', lowerIsBetter: false },
    { key: 'rmse', label: 'RMSE', lowerIsBetter: true },
    { key: 'mae', label: 'MAE', lowerIsBetter: true },
    { key: 'mse', label: 'MSE', lowerIsBetter: true },
    { key: 'mape', label: 'MAPE', lowerIsBetter: true }
  ];

  // Find best model for each metric
  const findBestModel = (metricKey: keyof ModelMetrics, lowerIsBetter: boolean): string | null => {
    const modelsWithValue = modelsWithMetrics.filter(m => m.metrics?.[metricKey] !== undefined);
    if (modelsWithValue.length === 0) return null;

    const sorted = modelsWithValue.sort((a, b) => {
      const aVal = a.metrics?.[metricKey] ?? (lowerIsBetter ? Infinity : -Infinity);
      const bVal = b.metrics?.[metricKey] ?? (lowerIsBetter ? Infinity : -Infinity);
      return lowerIsBetter ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });

    return sorted[0]?.filename || null;
  };

  if (selectedModels.length < 2) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <p className="text-yellow-700 text-sm">
          {t('training.sidebar.comparison.needMore', 'Select at least 2 models to compare')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm mb-4">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-border bg-muted rounded-t-lg">
        <h4 className="font-semibold text-foreground">
          {t('training.sidebar.comparison.title', 'Model Comparison')}
        </h4>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground p-1"
          title={t('training.sidebar.comparison.close', 'Close comparison')}
        >
          <FaTimes size={14} />
        </button>
      </div>

      {/* Content */}
      <div className="p-3">
        {loadingMetrics || metricsLoading ? (
          <div className="flex items-center justify-center py-8">
            <FaSpinner className="animate-spin text-primary mr-2" />
            <span className="text-foreground text-sm">
              {t('training.sidebar.comparison.loading', 'Loading metrics...')}
            </span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-2 text-muted-foreground font-medium">
                    {t('training.sidebar.comparison.metric', 'Metric')}
                  </th>
                  {modelsWithMetrics.map(model => (
                    <th key={model.filename} className="text-center py-2 px-2 text-foreground font-medium">
                      {model.filename.replace('.h5', '')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {metricDefinitions.map(({ key, label, lowerIsBetter }) => {
                  const bestModel = findBestModel(key, lowerIsBetter);
                  const hasAnyValue = modelsWithMetrics.some(m => m.metrics?.[key] !== undefined);

                  if (!hasAnyValue) return null;

                  return (
                    <tr key={key} className="border-b border-border last:border-0">
                      <td className="py-2 px-2 text-muted-foreground">{label}</td>
                      {modelsWithMetrics.map(model => {
                        const value = model.metrics?.[key];
                        const isBest = model.filename === bestModel;

                        return (
                          <td
                            key={model.filename}
                            className={`py-2 px-2 text-center ${
                              isBest ? 'bg-green-50 text-green-700 font-semibold' : 'text-foreground'
                            }`}
                          >
                            <div className="flex items-center justify-center gap-1">
                              {value !== undefined ? (
                                <>
                                  {(value as number).toFixed(4)}
                                  {isBest && <FaTrophy className="text-yellow-500" size={12} />}
                                </>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="px-3 pb-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <FaTrophy className="text-yellow-500" size={10} />
          <span>{t('training.sidebar.comparison.bestModel', 'Best performer')}</span>
        </div>
      </div>
    </div>
  );
});

ModelComparisonPanel.displayName = 'ModelComparisonPanel';

export default ModelComparisonPanel;
